import { AfterViewInit, ChangeDetectionStrategy, Component, Input, OnDestroy, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { CdkConnectedOverlay, ConnectedPosition } from "@angular/cdk/overlay";
import { OverlayRepositionService } from "../../../services/internal-services/overlay-reposition.service";
import { Viewerlayer } from "../../../model/viewerlayer";
import { LayerCommunicatorService } from "../../../services/communicators/layer-communicator.service";
import { ToolbarService } from "../../../services/internal-services/toolbar.service";
import { FeaturesSearchService } from "../../../services/internal-services/features-search.service";
import { BaseLayerManagerComponent } from "../base-layer-manager/base-layer-manager.component";
import { LayerService } from "../../../services/internal-services/layer.service";
import { ViewerConstants } from "../../../../shared/model/viewer-constants";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import {
  GoogleAnalyticsConstantsCategory,
  GoogleAnalyticsConstantsEvents,
} from "../../../../shared/model/google-analytics-constants";
import { IProjectAOI } from "../../../interfaces/i-project-aoi";
import { MapService } from "../../../services/internal-services/map.service";
import { FeatureFlagsService } from "../../../services/internal-services/feature-flags.service";
import { GoogleAnalyticsService } from "../../../../shared/services/google-analytics.service";
import { AddUserDataDialogComponent } from "../../../../dialog-components/add-user-data-dialog/add-user-data-dialog.component";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "app-layer-manager-widget",
  templateUrl: "./layer-manager-widget.component.html",
  styleUrls: ["./layer-manager-widget.component.scss"],
  standalone: false,
})
export class LayerManagerWidgetComponent extends BaseLayerManagerComponent implements AfterViewInit, OnDestroy {
  @ViewChild(CdkConnectedOverlay) private connectedOverlay!: CdkConnectedOverlay;

  private _overlayLayers: Viewerlayer[];

  private _resize: boolean = false;

  private _dualViewEnabled: boolean = false;

  private _aois: IProjectAOI[];

  private _collectionLayer: Viewerlayer[];

  imageryIcon = "assets/images/imagery-icon.png";

  baseMapIcon = "assets/images/basemap-icon.png";

  expandPanel: boolean = false;

  isImageryShown: boolean = true;

  @Input()
  isActivatedAutoContrast: boolean;

  /**
   * Custom overlay positions for cdkConnectedOverlay.
   * If not provided, uses default position (end/top -> end/bottom)
   */
  @Input()
  overlayPositions?: ConnectedPosition[];

  /**
   * Default overlay position configuration
   */
  readonly defaultOverlayPositions: ConnectedPosition[] = [
    {
      originX: "end",
      originY: "top",
      overlayX: "end",
      overlayY: "bottom",
    },
  ];

  autoContrastAllowed: boolean = false;

  @Input() set overlayLayers(layers: Viewerlayer[]) {
    this._overlayLayers = layers;
  }

  get overlayLayers(): Viewerlayer[] {
    return this._overlayLayers;
  }

  @Input() set resize(resize: boolean) {
    this._resize = resize;
  }

  get resize(): boolean {
    return this._resize;
  }

  @Input() set dualViewEnabled(enabled: boolean) {
    this._dualViewEnabled = enabled;
  }

  get dualViewEnabled(): boolean {
    return this._dualViewEnabled;
  }

  @Input()
  centerAOI: IProjectAOI;

  @Input() set aois(aois: IProjectAOI[]) {
    this._aois = aois;
    // todo: i dont know why we have to force to enabled contrast, I guess for create new layers for aois outside the viewport
    // we have to refactor and change this for the new vexcel multilayer
    this._aois
      ?.filter(
        (a) => a.layer && a.isGraySky() && a.hasImages() && this.isActivatedAutoContrast !== !!a.enabledContrast(),
      )
      .forEach((a) => this.viewerService.toggleContrastEnhance(a.getLayerId()));
  }

  get collectionLayer(): Viewerlayer[] {
    return this._collectionLayer;
  }

  @Input() set collectionLayer(value: Viewerlayer[]) {
    this._collectionLayer = value;

    if (!!this._collectionLayer && this._collectionLayer.length > 0) {
      const childLayerArray = this._collectionLayer.reduce((acc, obj) => {
        acc = acc.concat(obj.childs);
        return acc;
      }, []);

      const layers = [...this._collectionLayer, ...childLayerArray];

      this.autoContrastAllowed = !!layers.find(
        (l) => l.config?.enhancements?.includes(ViewerConstants.LAYER_ENHANCEMENTS.COLOR_STRETCH),
      );
    }
  }

  constructor(
    protected googleAnalyticsService: GoogleAnalyticsService,
    protected mapService: MapService,
    protected layerCommunicatorService: LayerCommunicatorService,
    protected matDialog: MatDialog,
    protected toolbarService: ToolbarService,
    protected featuresSearchService: FeaturesSearchService,
    protected layerService: LayerService,
    protected flagService: FeatureFlagsService,
    private overlayRepositionService: OverlayRepositionService,
  ) {
    super(
      googleAnalyticsService,
      mapService,
      layerCommunicatorService,
      matDialog,
      toolbarService,
      featuresSearchService,
      layerService,
      flagService,
    );
  }

  showUploadDialog() {
    const dialog = this.matDialog.open(AddUserDataDialogComponent, {
      data: {
        title: "Add WMS or WMTS layer from URL",
        viewerService: this.viewerService,
      },
      panelClass: "medium-panel-dialog",
    });
    dialog.afterClosed().subscribe(() => {});
  }

  ngAfterViewInit() {
    this.subscriptions.push(
      this.mapService.mapInitialized$.subscribe((initialized) => {
        if (initialized && this.mapService.getOverlayManager().getVexcelSources()) {
          this.autoContrastAllowed = this.mapService.getOverlayManager().getVexcelSources().includes("graysky");
        }
      }),
    );
    this.overlayRepositionService.register(this.connectedOverlay);
  }

  ngOnDestroy() {
    this.overlayRepositionService.unregister(this.connectedOverlay);
    super.ngOnDestroy();
  }

  togglePanel() {
    this.expandPanel = !this.expandPanel;

    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      this.expandPanel
        ? GoogleAnalyticsConstantsEvents.OVERLAYS_WIDGET_EXPAND
        : GoogleAnalyticsConstantsEvents.OVERLAYS_WIDGET_COLLAPSE,
      GoogleAnalyticsConstantsCategory.WIDGET,
      this.expandPanel,
    );
  }

  changeOverlayOrder(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.overlayLayers, event.previousIndex, event.currentIndex);
    this.viewerService.updateOverlayOrder(this.overlayLayers);
  }

  changeChildVisibility(event: any) {
    const { layer } = event;
    const { child } = event;
    const { visibility } = event;

    if (child) child.visible = visibility;
    // Update parent layer object status
    layer.visible = !!layer.layersAvailable?.find((l) => l.visible) || !!layer.childs?.find((l) => l.visible);
    // Update layers on map
    if (layer?.layerType === ViewerConstants.LAYER_TYPE.WMSLAYER) {
      this.mapService.getCoreMap().changeVisibilityWMSChild(layer.id);
    } else {
      this.viewerService.changeLayerVisibility(child.id, visibility);
    }
    this.viewerService.saveUserConfiguration(this.viewerService.getLayersToSaveConfiguration());
  }

  changeVisibility(event: any) {
    const { layer } = event;

    if (layer.owner) {
      this.viewerService.changeUserLayerVisibility(event.layer.id, event.visibility);
      this.viewerService.updateUserLayer(event.layer);
    } else {
      this.viewerService.changeLayerVisibility(event.layer.id, event.visibility, false);

      if (layer?.layerType === ViewerConstants.LAYER_TYPE.WMSLAYER && layer.layersAvailable) {
        layer.layersAvailable.map((layerAvailable) => (layerAvailable.visible = event.visibility));
        this.changeWMSChildVisibility(null, null, layer);
      } else {
        layer.childs?.map((child) => this.viewerService.changeLayerVisibility(child.id, event.visibility, false));
      }
    }
    this.viewerService.saveUserConfiguration(this.viewerService.getLayersToSaveConfiguration());
    this.viewerService.createStateParameter();
    this.viewerService.updateUrlParameters();
  }

  showImagery() {
    this.viewerService.getCollections().forEach((c) => this.viewerService.changeAOIVisibility(c.id, true, true));
    const vexcelBaseLayer = this.mapService.getOverlayManager().getVexcelBaseLayer();
    if (vexcelBaseLayer) {
      vexcelBaseLayer.setVisible(true);
    }
    this.isImageryShown = true;
    this.viewerService.saveUserConfiguration(this.viewerService.getLayersToSaveConfiguration());
    this.viewerService.setActivatedBaseMap(false);
    this.viewerService.createStateParameter();
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.OVERLAYS_WIDGET_IMAGERY,
      GoogleAnalyticsConstantsCategory.WIDGET,
      this.isImageryShown,
    );
  }

  showBasemap() {
    this.viewerService.getCollections().forEach((c) => this.viewerService.changeAOIVisibility(c.id, false, true));
    const vexcelBaseLayer = this.mapService.getOverlayManager().getVexcelBaseLayer();
    if (vexcelBaseLayer) {
      vexcelBaseLayer.setVisible(false);
    }
    this.isImageryShown = false;
    this.viewerService.saveUserConfiguration(this.viewerService.getLayersToSaveConfiguration());
    this.viewerService.setActivatedBaseMap(true);
    this.viewerService.createStateParameter();
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.OVERLAYS_WIDGET_BASEMAP,
      GoogleAnalyticsConstantsCategory.WIDGET,
      this.isImageryShown,
    );
  }

  toggleAutoContrast(checked: boolean) {
    this.isActivatedAutoContrast = checked;

    this.viewerService.toggleEnhance(this.isActivatedAutoContrast);
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      this.isActivatedAutoContrast
        ? GoogleAnalyticsConstantsEvents.GRAY_SKY_CONTRAST_ON
        : GoogleAnalyticsConstantsEvents.GRAY_SKY_CONTRAST_OFF,
      GoogleAnalyticsConstantsCategory.WIDGET,
      this.isActivatedAutoContrast,
    );
  }
}
